import { createContext, useContext, type ReactNode } from "react";

/**
 * The owner directory the host shell injects (PB-94), keeping this feature
 * package free of any auth coupling (ADR-0009: the shell is the composition
 * root).
 *
 * The owner fields and every id → name lookup read the host's `PeopleSource`
 * (`usePeopleSource`, assign-person consistency); only the directory groups
 * are left here.
 */
export interface KpiOwnerDirectory {
  /**
   * The tenant's directory groups, for narrowing the owner picker (PB-138).
   *
   * OPTIONAL: a host that cannot supply groups omits it and the picker renders
   * no filter. An EMPTY array means the same thing — a tenant legitimately has
   * no directory groups when its administrator has not granted
   * `GroupMember.Read.All` and it provisions none over SCIM — so neither case
   * is an error, and neither shows a control that cannot narrow anything.
   */
  readonly loadGroups?: (
    signal?: AbortSignal,
  ) => Promise<readonly ManagedKpiOwnerGroup[]>;
}

/** One directory group offered by the owner picker's optional filter. */
export interface ManagedKpiOwnerGroup {
  readonly id: string;
  readonly name: string;
}

const KpiOwnerDirectoryContext = createContext<KpiOwnerDirectory | null>(null);

export function KpiOwnerDirectoryProvider({
  value,
  children,
}: {
  value: KpiOwnerDirectory;
  children: ReactNode;
}): JSX.Element {
  return (
    <KpiOwnerDirectoryContext.Provider value={value}>
      {children}
    </KpiOwnerDirectoryContext.Provider>
  );
}

export function useKpiOwnerDirectory(): KpiOwnerDirectory {
  const ctx = useContext(KpiOwnerDirectoryContext);
  if (!ctx) {
    throw new Error(
      "useKpiOwnerDirectory: KpiOwnerDirectoryProvider missing. Mount <KpiManager> with an ownerDirectory (the shell supplies it).",
    );
  }
  return ctx;
}
